import { useEffect } from 'react'
import { Tooltip, Badge, Group } from '@mantine/core'
import { IconAlertTriangle, IconClockOff } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'

// Inject pulse keyframes once
const PULSE_STYLE_ID = 'sla-pulse-keyframes'
function ensurePulseKeyframes() {
  if (typeof document === 'undefined') return
  if (document.getElementById(PULSE_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = PULSE_STYLE_ID
  style.textContent = `
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }
  `
  document.head.appendChild(style)
}

interface SlaIndicatorProps {
  createdAt: string
  slaResolutionHours: number | null | undefined
  resolvedAt: string | null
  closedAt: string | null
  size?: 'xs' | 'sm'
  /** Se fornecido, busca dados reais de SLA da API com polling */
  taskRequestId?: number
  tenantId?: number
}

interface SlaApiData {
  sla_configured: boolean
  sla_resolution_hours?: number
  deadline_resolution?: string
  is_breached?: boolean
  is_at_risk?: boolean
  remaining_seconds?: number
  deadline_first_response?: string | null
  /** TASK-10951: passou do prazo de 1ª resposta sem ninguém do atendimento responder. */
  first_response_breached?: boolean
}

function formatDuration(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)}min`
  if (hours < 24) return `${hours.toFixed(1)}h`
  return `${(hours / 24).toFixed(1)}d`
}

export function SlaIndicator({
  createdAt, slaResolutionHours, resolvedAt, closedAt, size = 'xs',
  taskRequestId, tenantId,
}: SlaIndicatorProps) {
  useEffect(() => { ensurePulseKeyframes() }, [])

  // Buscar SLA real da API (com polling a cada 60s) se IDs fornecidos
  const { data: slaApi } = useQuery<SlaApiData>({
    queryKey: ['sla-info', tenantId, taskRequestId],
    queryFn: async () => (await api.get<SlaApiData>(`/v1/tenants/${tenantId}/workflow/${taskRequestId}/sla`)).data,
    enabled: !!taskRequestId && !!tenantId,
    refetchInterval: 60_000,
  })

  // Se API retornou dados, usar eles
  if (slaApi?.sla_configured) {
    const isDone = !!resolvedAt || !!closedAt
    const isBreached = !!slaApi.is_breached
    const isAtRisk = !!slaApi.is_at_risk
    const remainingHours = (slaApi.remaining_seconds ?? 0) / 3600

    if (isDone) {
      const color = isBreached ? 'red' : 'teal'
      const label = isBreached ? 'SLA vencido' : 'Dentro do SLA'
      return <Tooltip label={label} withArrow><Badge size={size} color={color} variant="light">{label}</Badge></Tooltip>
    }

    const color = isBreached ? 'red' : isAtRisk ? 'orange' : 'green'
    const label = isBreached
      ? `Vencido há ${formatDuration(-remainingHours)}`
      : `${formatDuration(remainingHours)} restantes`
    const resolucao = (
      <Tooltip label={`SLA: ${label}${slaApi.deadline_resolution ? ` (deadline: ${new Date(slaApi.deadline_resolution).toLocaleString('pt-BR')})` : ''}`} withArrow>
        <Badge size={size} color={color} variant={isBreached ? 'filled' : 'light'}
          leftSection={isBreached || isAtRisk ? <IconAlertTriangle size={8} /> : undefined}
          style={isBreached ? { animation: 'pulse 2s infinite' } : undefined}>{label}</Badge>
      </Tooltip>
    )
    if (!slaApi.first_response_breached) return resolucao
    // Estado "Vencido" da seção 2.6 do CHAMADOS_DESIGN_SYSTEM (red + IconClockOff),
    // aplicado ao prazo de 1ª resposta: sem ele o chamado sem resposta aparecia verde.
    return (
      <Group gap={4} wrap="nowrap">
        {resolucao}
        <Tooltip
          label={`Ninguém do atendimento respondeu dentro do prazo de 1ª resposta${
            slaApi.deadline_first_response
              ? ` (prazo: ${new Date(slaApi.deadline_first_response).toLocaleString('pt-BR')})`
              : ''}`}
          withArrow
        >
          <Badge size={size} color="red" variant="light" leftSection={<IconClockOff size={8} />}>
            1ª resposta vencida
          </Badge>
        </Tooltip>
      </Group>
    )
  }

  // Fallback: calculo local
  if (!slaResolutionHours) return null

  const created = new Date(createdAt).getTime()
  const deadline = created + slaResolutionHours * 3600 * 1000
  const end = resolvedAt ? new Date(resolvedAt).getTime() : closedAt ? new Date(closedAt).getTime() : null
  const now = Date.now()
  const reference = end ?? now

  const remainingMs = deadline - reference
  const remainingHours = remainingMs / 3600 / 1000
  const pctRemaining = remainingHours / slaResolutionHours

  const isBreached = remainingMs < 0
  const isAtRisk = !isBreached && pctRemaining < 0.2
  const isDone = !!end

  if (isDone) {
    const color = isBreached ? 'red' : 'teal'
    const label = isBreached ? 'SLA vencido' : 'Dentro do SLA'
    return (
      <Tooltip label={label} withArrow>
        <Badge size={size} color={color} variant="light">{label}</Badge>
      </Tooltip>
    )
  }

  const color = isBreached ? 'red' : isAtRisk ? 'orange' : 'green'
  const label = isBreached
    ? `Vencido há ${formatDuration(-remainingHours)}`
    : `${formatDuration(remainingHours)} restantes`

  return (
    <Tooltip
      label={`SLA: ${label} (deadline: ${new Date(deadline).toLocaleString('pt-BR')})`}
      withArrow
    >
      <Badge
        size={size}
        color={color}
        variant={isBreached ? 'filled' : 'light'}
        leftSection={isBreached || isAtRisk ? <IconAlertTriangle size={8} /> : undefined}
        style={isBreached ? { animation: 'pulse 2s infinite' } : undefined}
      >
        {label}
      </Badge>
    </Tooltip>
  )
}
